import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Avatar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../components/ui/PageLayout';
import { useSnackbar } from '../contexts/SnackbarContext';
import { DynamicJsonForm } from '../components/ui';

import { log } from '../utils/errors/logger';
import { getApiFetchUrl } from '../config/apiConfig';
import { getCsrfToken, requiresCsrfToken } from '../utils/auth/csrfManager';
import { getProviderIcon, getProviderColor } from '../utils/documents/documentSourceHelpers';
import { formatDate } from '../utils/formatting/formatters';
import {
  useAdminProviderConfigs,
  useCreateProviderConfig,
  useUpdateProviderConfig,
  useDeleteProviderConfig,
  useSyncProviderConfig,
  useAvailableProviders,
} from '../hooks/api/useDocumentSources';
import type {
  DocumentSourceProviderConfigRequest,
  DocumentSourceProviderConfigResponse,
} from '../api/data-contracts';

const PFX_FIELD_KEYS = [
  'certificate_private_key',
  'certificate_thumbprint',
  'certificate_public_certificate',
] as const;

const schemaSupportsPfxUpload = (schema?: Record<string, unknown>): boolean => {
  const props = (schema?.properties ?? {}) as Record<string, unknown>;
  return PFX_FIELD_KEYS.every((key) => key in props);
};

interface PfxUploadPanelProps {
  onExtracted: (fields: {
    certificate_private_key: string;
    certificate_public_certificate: string;
    certificate_thumbprint: string;
  }) => void;
}

const PfxUploadPanel: React.FC<PfxUploadPanelProps> = ({ onExtracted }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) { return; }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      if (passphrase) {
        body.append('passphrase', passphrase);
      }
      const headers: Record<string, string> = {};
      if (requiresCsrfToken('POST')) {
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          headers['X-CSRF-Token'] = csrfToken;
        }
      }
      const response = await fetch(`${getApiFetchUrl()}/document-sources/admin/parse-pfx`, {
        method: 'POST',
        headers,
        body,
        credentials: 'include',
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail?.detail || `HTTP ${response.status}`);
      }
      const data = await response.json() as {
        private_key_pem: string;
        public_certificate_pem: string;
        thumbprint: string;
      };
      onExtracted({
        certificate_private_key: data.private_key_pem,
        certificate_public_certificate: data.public_certificate_pem,
        certificate_thumbprint: data.thumbprint,
      });
      setPassphrase('');
      setFile(null);
      showSuccess(t('documentSourceConfig.pfxUpload.success'));
    } catch (error) {
      log.error('PFX parse failed', { error });
      const message = error instanceof Error ? error.message : '';
      showError(message || t('documentSourceConfig.pfxUpload.failed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle1" gutterBottom>
        {t('documentSourceConfig.pfxUpload.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('documentSourceConfig.pfxUpload.description')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadIcon />}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('documentSourceConfig.pfxUpload.selectFile')}
          <input
            type="file"
            hidden
            accept=".pfx,.p12,application/x-pkcs12"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Button>
        {file && (
          <Typography variant="caption" color="text.secondary">
            {t('documentSourceConfig.pfxUpload.selectedFile', { name: file.name })}
          </Typography>
        )}
        <TextField
          label={t('documentSourceConfig.pfxUpload.passphraseLabel')}
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          size="small"
          autoComplete="off"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!file || uploading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {uploading
            ? <CircularProgress size={20} sx={{ color: 'inherit' }} />
            : t('documentSourceConfig.pfxUpload.uploadButton')}
        </Button>
      </Box>
    </Box>
  );
};

type AvailableProviderMeta = {
  provider_key: string;
  display_name?: string;
  name?: string;
  capabilities?: {
    external_acl_mode?: string;
    [k: string]: unknown;
  };
  schemas?: {
    admin_config_schema?: Record<string, unknown>;
    user_config_schema?: Record<string, unknown>;
    ui_schema?: Record<string, unknown>;
  };
};

const DocumentSourceConfig: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  
  // Queries
  const { data: configs = [], isLoading: configsLoading } = useAdminProviderConfigs();
  const { data: availableProvidersRaw = [], isLoading: providersLoading } = useAvailableProviders();
  const availableProviders = availableProvidersRaw as unknown as AvailableProviderMeta[];
  
  // Mutations
  const createProviderConfig = useCreateProviderConfig();
  const updateProviderConfig = useUpdateProviderConfig();
  const deleteProviderConfig = useDeleteProviderConfig();
  const syncProviderConfig = useSyncProviderConfig();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<DocumentSourceProviderConfigResponse | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<DocumentSourceProviderConfigResponse | null>(null);

  // Form state
  const [formData, setFormData] = useState<DocumentSourceProviderConfigRequest>({
    provider_type: '',
    provider_key: '',
    name: '',
    description: null,
    enabled: true,
    provider_config: {},
    initial_connection: null,
  });

  // Selected provider metadata for schema
  const selectedProviderMeta = availableProviders.find(p => p.provider_key === formData.provider_key);
  const selectedSupportsExternalAcl =
    !!selectedProviderMeta?.capabilities?.external_acl_mode &&
    selectedProviderMeta.capabilities.external_acl_mode !== 'none';

  const handleOpenDialog = (config?: DocumentSourceProviderConfigResponse) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        provider_type: config.provider_type,
        provider_key: config.provider_key,
        name: config.name,
        description: config.description ?? null,
        enabled: config.enabled,
        provider_config: {}, // We don't load existing config values for security (secrets)
        initial_connection: null, // Initial connection is only relevant on creation
      });
    } else {
      setEditingConfig(null);
      // Default to first available provider if any
      const firstProvider = availableProviders[0]?.provider_key || '';

      setFormData({
        provider_type: firstProvider,
        provider_key: firstProvider,
        name: '',
        description: null,
        enabled: true,
        provider_config: {},
        initial_connection: null,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
  };

  const handleSaveConfig = async () => {
    try {
      if (editingConfig) {
        await updateProviderConfig.mutateAsync({
          configId: editingConfig.id,
          data: formData,
        });
      } else {
        await createProviderConfig.mutateAsync(formData);
      }
      if (selectedSupportsExternalAcl && formData.initial_connection) {
        showSuccess(t('documentSourceConfig.messages.saveAndSyncStarted'));
      } else {
        showSuccess(t('documentSourceConfig.messages.saveSuccess'));
      }

      handleCloseDialog();
    } catch (error) {
      log.error('Error saving provider config', { error });
      showError(t('documentSourceConfig.messages.saveFailed'));
    }
  };

  const handleSyncConfig = async (config: DocumentSourceProviderConfigResponse) => {
    try {
      const result = await syncProviderConfig.mutateAsync(config.id);
      showSuccess(
        t('documentSourceConfig.messages.syncEnqueued', {
          count: result.connection_count,
        })
      );
    } catch (error: any) {
      log.error('Error triggering provider config sync', { error });
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      showError(detail || t('documentSourceConfig.messages.syncFailed'));
    }
  };

  const handleDeleteConfig = async () => {
    if (!configToDelete) { return; }

    try {
      await deleteProviderConfig.mutateAsync(configToDelete.id);
      showSuccess(t('documentSourceConfig.messages.deleteSuccess'));
      setDeleteConfirmOpen(false);
      setConfigToDelete(null);
    } catch (error: any) {
      log.error('Error deleting provider config', { error });

      if (error?.response?.status === 400 && error?.response?.data?.error_code === 'ACTIVE_CONNECTIONS_EXIST') {
        const activeCount = error?.response?.data?.active_connections || 0;
        const errorMsg = t('documentSourceConfig.messages.deleteFailedActiveConnections', { count: activeCount });
        showError(errorMsg);
      } else if (error?.response?.data?.message) {
        showError(error.response.data.message);
      } else {
        showError(t('documentSourceConfig.messages.deleteFailed'));
      }

      setDeleteConfirmOpen(false);
      setConfigToDelete(null);
    }
  };

  const formatProviderType = (config: DocumentSourceProviderConfigResponse): string => {
    const meta = availableProviders.find(p => p.provider_key === config.provider_key);
    if (meta) {
      return meta.display_name || meta.name || config.provider_key;
    }
    return config.provider_key;
  };

  const isLoading = configsLoading || providersLoading;

  if (isLoading && configs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <PageLayout
        title={t('documentSourceConfig.title')}
        headerActions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={availableProviders.length === 0}
          >
            {t('documentSourceConfig.addProvider')}
          </Button>
        }
        isLoading={isLoading}
        maxWidth="xl"
      >
        {configs.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" color="text.secondary">
              {t('documentSourceConfig.noProviders')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {configs.map((config) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={config.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: getProviderColor(config.provider_key), mr: 2 }}>
                        {getProviderIcon(config.provider_key)}
                      </Avatar>
                      <Box flexGrow={1}>
                        <Typography variant="h6">{config.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatProviderType(config)}
                        </Typography>
                      </Box>
                      <Chip
                        label={config.enabled ? t('common.enabled') : t('common.disabled')}
                        color={config.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    {config.description && (
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        {config.description}
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      {t('common.createdAt')}: {formatDate(config.created_at, i18n.language)}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(config)}
                      color="primary"
                      title={t('documentSourceConfig.editProvider')}
                      aria-label={t('documentSourceConfig.editProvider')}
                    >
                      <EditIcon />
                    </IconButton>
                    {(() => {
                      const meta = availableProviders.find(p => p.provider_key === config.provider_key);
                      const supportsSync = !!meta?.capabilities?.external_acl_mode &&
                        meta.capabilities.external_acl_mode !== 'none';
                      if (!supportsSync) { return null; }
                      const isSyncing = syncProviderConfig.isPending &&
                        syncProviderConfig.variables === config.id;
                      return (
                        <IconButton
                          size="small"
                          onClick={() => handleSyncConfig(config)}
                          color="primary"
                          disabled={isSyncing || !config.enabled}
                          title={t('documentSourceConfig.syncNow')}
                          aria-label={t('documentSourceConfig.syncNow')}
                        >
                          {isSyncing ? <CircularProgress size={20} /> : <SyncIcon />}
                        </IconButton>
                      );
                    })()}
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfigToDelete(config);
                        setDeleteConfirmOpen(true);
                      }}
                      color="error"
                      title={t('documentSourceConfig.deleteProvider')}
                      aria-label={t('documentSourceConfig.deleteProvider')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </PageLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConfig
            ? t('documentSourceConfig.editProvider')
            : t('documentSourceConfig.addProvider')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }} key="provider-key">
                <FormControl fullWidth>
                  <InputLabel>{t('documentSourceConfig.providerType')}</InputLabel>
                  <Select
                    value={formData.provider_key}
                    onChange={(e) => {
                      const key = e.target.value as string;
                      setFormData({ 
                        ...formData, 
                        provider_key: key,
                        provider_type: key
                      });
                    }}
                    disabled={Boolean(editingConfig)}
                  >
                    {availableProviders.map((provider) => (
                      <MenuItem key={provider.provider_key} value={provider.provider_key}>
                        {provider.display_name || provider.name || provider.provider_key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }} key="name">
                <TextField
                  fullWidth
                  label={t('common.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }} key="description">
                <TextField
                  fullWidth
                  label={t('common.description')}
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid size={{ xs: 12 }} key="enabled">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enabled}
                      onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    />
                  }
                  label={t('common.enabled')}
                />
              </Grid>

              {selectedProviderMeta?.schemas?.admin_config_schema && (
                <Grid size={{ xs: 12 }} key="config-divider">
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('documentSourceConfig.providerConfiguration')}
                  </Typography>
                  <DynamicJsonForm
                    schema={selectedProviderMeta.schemas.admin_config_schema}
                    value={formData.provider_config || {}}
                    onChange={(config) => setFormData({ ...formData, provider_config: config })}
                  />
                  {schemaSupportsPfxUpload(selectedProviderMeta.schemas.admin_config_schema) && (
                    <PfxUploadPanel
                      onExtracted={(fields) => setFormData((prev) => ({
                        ...prev,
                        provider_config: { ...(prev.provider_config || {}), ...fields },
                      }))}
                    />
                  )}
                </Grid>
              )}

              {selectedSupportsExternalAcl &&
                selectedProviderMeta?.schemas?.user_config_schema && (
                <Grid size={{ xs: 12 }} key="initial-connection">
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('documentSourceConfig.initialConnectionTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('documentSourceConfig.initialConnectionHint')}
                  </Typography>
                  <DynamicJsonForm
                    schema={selectedProviderMeta.schemas.user_config_schema}
                    value={(formData.initial_connection || {}) as Record<string, unknown>}
                    onChange={(value) => setFormData({
                      ...formData,
                      initial_connection: value && Object.keys(value).length > 0 ? value : null,
                    })}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveConfig} variant="contained" color="primary">
            {editingConfig ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('documentSourceConfig.confirmDeleteProvider', {
              name: configToDelete?.name || ''
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfig} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentSourceConfig;
